/**
 * @fileoverview Achievement-by-team progress bars.
 * @description One row per team of the Sales Performance summary's comparison lines: the team's
 * name, how many of its assigned targets were achieved, the percentage, and a bar of that width.
 * Shows an empty state when the selection has no team.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { formatPercent } from '@/features/sales/lib/sales-performance.utils'

import TableEmptyState from './TableEmptyState'

import type { SalesPerformanceTeamLine } from '@/features/sales/types/sales-performance.types'

interface PerformanceTeamBarsProps {
  teams: SalesPerformanceTeamLine[]
}

/**
 * @component
 * @param {PerformanceTeamBarsProps} props - The teams to compare.
 * @param {SalesPerformanceTeamLine[]} props.teams - One comparison line per team.
 */
function PerformanceTeamBars({ teams }: PerformanceTeamBarsProps) {
  const { t } = useTranslation()

  if (teams.length === 0) {
    return (
      <TableEmptyState
        message={t('sales.salesPerformance.teams.emptyTitle')}
        description={t('sales.salesPerformance.teams.emptyDescription')}
        icon="users"
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {teams.map((team) => (
        <div
          key={team.teamKey}
          className="flex flex-col gap-1.5"
          data-testid="sales-performance-team-row"
        >
          <div className="flex items-baseline justify-between gap-3 text-compact">
            <span
              className="truncate font-semibold text-ink"
              data-testid="sales-performance-team-name"
            >
              {team.label}
            </span>
            <span className="text-slate tabular-nums" data-testid="sales-performance-team-line">
              {t('sales.salesPerformance.teams.line', {
                achieved: team.achieved,
                assigned: team.assigned,
              })}
              {' · '}
              <b className="text-ink" data-testid="sales-performance-team-percent">
                {formatPercent(team.achievementPercent)}
              </b>
            </span>
          </div>
          <div className="h-3.5 overflow-hidden rounded bg-input-bg">
            <div
              className="h-full bg-info"
              style={{ width: `${String(team.achievementPercent ?? 0)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default PerformanceTeamBars
